import { useCallback, useEffect, useState } from 'react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import QueuedExtensionSettings from './QueuedExtensionSettings';
import ConnectedExtensionSettings from './ConnectedExtensionSettings';
import ProxyListsSettings from './ProxyListsSettings';
import FetcherCard from '../components/network/FetcherCard';
import FetcherTabs from '../components/network/FetcherTabs';
import AccessTokens from '../components/network/AccessTokens';
import ToggleRow from '../components/network/ToggleRow';
import AdapterCard from '../components/network/AdapterCard';
import PausedEngines from '../components/network/PausedEngines';
import type {
  AdapterChoice,
  AdaptersResponse,
  EngineOption,
  FetcherStatus,
  NetworkFetchersResponse,
  NetworkSettings,
  NetworkSettingsUpdate,
  NetworkStatus,
} from '../types';

/** THE NETWORK — one, run by admins for everyone.
 *
 *  Every fetch goes on one queue, and a dispatcher hands each request to the least busy
 *  adapter allowed to run it (plain HTTP, crawl4ai, Serper, the browser plugins), moving it to
 *  another when one fails. So the screen is: each fetcher's setup, the proxy lists they draw
 *  on, which request types each adapter may run, and what the queue is doing. Nobody else
 *  has network settings; what a user brings is their own browser, which is used before the
 *  shared ones — so that's the whole of the non-admin view. */
export default function NetworkSettingsPage() {
  const { user } = useAuth();
  return user?.is_admin ? <AdminNetwork /> : <UserNetwork />;
}

// ─────────────────────────────── shared bits ────────────────────────────────

const input =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500';

function Section({ title, hint, children }: { title: string; hint?: string; children: React.ReactNode }) {
  return (
    <section className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
      <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">{title}</h3>
        {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{hint}</p>}
      </div>
      <div className="p-4">{children}</div>
    </section>
  );
}

function Field({ label, children, hint }: { label: string; children: React.ReactNode; hint?: string }) {
  return (
    <div>
      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{hint}</p>}
    </div>
  );
}

// ─────────────────────────────── non-admin ────────────────────────────────

/** A title over each extension's setup, with the dot its toolbar icon carries.
 *  What each one does is said once, in its "Get the extension" block. */
function ExtensionHeading({ title, dot }: { title: string; dot: string }) {
  return (
    <h3 className="pt-2 flex items-center gap-2 text-base font-semibold text-gray-900 dark:text-gray-100">
      <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`} aria-hidden />
      {title}
    </h3>
  );
}

function UserNetwork() {
  const [status, setStatus] = useState<NetworkStatus | null>(null);
  useEffect(() => {
    api.getNetworkStatus().then(setStatus).catch(() => setStatus(null));
  }, []);
  return (
    <div className="max-w-2xl space-y-6">
      {status && (
        <div className="rounded-lg bg-gray-50 dark:bg-gray-900/40 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
          {status.network_enabled ? (
            <>
              <p>
                <span className="font-medium text-gray-900 dark:text-gray-100">Search:</span>{' '}
                {status.search_source}
                {'  ·  '}
                <span className="font-medium text-gray-900 dark:text-gray-100">Pages:</span>{' '}
                {status.page_rungs.join(', ') || status.search_source}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                The network is run by your admins. Your own browser is used first when it's
                connected
                {status.your_browsers_connected
                  ? ` (${status.your_browsers_connected} connected now)`
                  : ''}
                ; otherwise shared browsers and the rest of the network fetch for you.
              </p>
            </>
          ) : (
            <p>Networking is switched off by your admins.</p>
          )}
        </div>
      )}
      {/* Two extensions, either or both in one Chrome. Neither is the old one:
          the connected one answers straight away; the queued one works through
          a backlog while nobody is watching. */}
      {/* One token works in both, so it is offered once here, not in each card. */}
      <AccessTokens />
      <ExtensionHeading title="Connected extension" dot="bg-green-600" />
      <ConnectedExtensionSettings showTokens={false} />
      <ExtensionHeading title="Queued extension" dot="bg-orange-500" />
      <QueuedExtensionSettings showTokens={false} />
    </div>
  );
}

// ─────────────────────────────── admin ────────────────────────────────

const BROWSER_TYPES = ['chromium', 'firefox', 'webkit'];
// Legacy mode: the one fetcher that does everything.
function AdminNetwork() {
  const [settings, setSettings] = useState<NetworkSettings | null>(null);
  const [form, setForm] = useState<NetworkSettingsUpdate>({});
  const [fetchers, setFetchers] = useState<NetworkFetchersResponse | null>(null);
  const [adapters, setAdapters] = useState<AdaptersResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const loadFetchers = useCallback(async () => {
    try {
      const [f, a] = await Promise.all([api.getNetworkFetchers(), api.getNetworkAdapters()]);
      setFetchers(f);
      setAdapters(a);
    } catch {
      setFetchers(null);
    }
  }, []);

  const fill = (data: NetworkSettings) => {
    setSettings(data);
    setForm({
      is_network_enabled: data.is_network_enabled,
      search_engine: data.search_engine,
      search_engines: data.search_engines ?? [],
      engine_mode: data.engine_mode ?? 'SPREAD',
      browser_type: data.browser_type,
      downloader_type: data.downloader_type,
      crawler_host: data.crawler_host ?? '',
      batch_size: data.batch_size,
      sleep_seconds_after_captcha: data.sleep_seconds_after_captcha,
      sleep_seconds_between_batches: data.sleep_seconds_between_batches,
      desired_serp_count: data.desired_serp_count,
      use_cache: data.use_cache,
      recrawl_after_days: data.recrawl_after_days,
      fetch_timeout_seconds: data.fetch_timeout_seconds ?? 60,
      shared_block_hours: data.shared_block_hours ?? 168,
      fetch_adapters: data.fetch_adapters ?? {},
      browser_render_pages: data.browser_render_pages,
      extract_page_facts: data.extract_page_facts,
      serper_api_key: '', // empty = leave the stored key unchanged
      serper_country: data.serper_country ?? 'us',
      serper_language: data.serper_language ?? 'en',
      serper_location: data.serper_location ?? '',
    });
  };

  useEffect(() => {
    Promise.all([api.getNetworkSettings(), loadFetchers()])
      .then(([data]) => fill(data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load the network'))
      .finally(() => setLoading(false));
  }, [loadFetchers]);

  const setField = (key: keyof NetworkSettingsUpdate, value: NetworkSettingsUpdate[typeof key]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const setAdapter = (key: string, next: AdapterChoice) =>
    setForm((f) => ({ ...f, fetch_adapters: { ...(f.fetch_adapters ?? {}), [key]: next } }));

  const save = async () => {
    setSaving(true);
    setSaved(false);
    setError('');
    try {
      const payload: NetworkSettingsUpdate = {
        ...form,
        crawler_host: form.crawler_host?.trim() || null,
        serper_location: form.serper_location?.trim() || null,
      };
      if (!payload.serper_api_key?.trim()) delete payload.serper_api_key;
      // The adapters decide who fetches what now; the old single-fetcher choice stays as saved.
      delete payload.downloader_type;
      fill(await api.updateNetworkSettings(payload));
      await loadFetchers();
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>;

  const byKey: Record<string, FetcherStatus | undefined> = Object.fromEntries(
    (fetchers?.fetchers ?? []).map((f) => [f.key, f]),
  );
  // An engine can be in the pool when some adapter that's on may search it.
  const chosen = form.fetch_adapters ?? {};
  const searchable = new Set(
    Object.values(chosen)
      .filter((c) => c.on)
      .flatMap((c) => c.types)
      .filter((t) => t.startsWith('search/'))
      .map((t) => t.slice('search/'.length)),
  );
  const engineOptions: EngineOption[] = (settings?.engine_options ?? []).map((o) => ({
    ...o,
    supported: searchable.has(o.engine.toLowerCase()),
    note: searchable.has(o.engine.toLowerCase()) ? o.note : 'No adapter is allowed to search this engine.',
  }));

  // ONE SETTING, BOTH EXTENSIONS: shown on each one's tab, so it is there
  // whichever you are looking at. Changing it on one changes it on the other.
  const renderPagesToggle = (
    <div className="-mx-4 mb-3">
      <ToggleRow
        title="Open a tab to render pages (both extensions)"
        description="Page fetches that reach a browser load in a background tab so their JavaScript runs. Off = a raw fetch from the browser: faster, no tabs, but JavaScript-built pages come back empty. Applies to the connected and the queued extension."
        checked={form.browser_render_pages !== false}
        onChange={(v) => setField('browser_render_pages', v)}
      />
    </div>
  );

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex justify-between items-baseline">
        <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Network</h2>
        {settings && (
          <span className="text-xs text-gray-400 dark:text-gray-500">
            Used by every user · last changed {new Date(settings.updated_at).toLocaleString()}
          </span>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded">
          {error}
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
        <ToggleRow
          title="All fetching on"
          description="Master off-switch. Turn this off only to stop every search and page fetch for every user - for an outage or an emergency. It is not how you choose a fetcher; that's below."
          checked={form.is_network_enabled !== false}
          onChange={(v) => setField('is_network_enabled', v)}
        />
      </div>

      {/* ───────────── Fetchers ───────────── */}
      <Section title="Fetchers" hint="What the network can fetch with, and each one's setup.">
        <FetcherTabs>
          <FetcherCard title="Plain HTTP" fetcher={byKey.Http} />

          <FetcherCard title="crawl4ai" fetcher={byKey.Crawl4ai}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field
                  label="Crawler host"
                  hint={
                    settings?.crawler_host_from_env
                      ? 'Blank here: the HOUSE_CRAWLER_HOST environment value is in use.'
                      : undefined
                  }
                >
                  <input
                    value={form.crawler_host ?? ''}
                    onChange={(e) => setField('crawler_host', e.target.value)}
                    placeholder="e.g. http://crawl4ai:11235"
                    className={`${input} font-mono`}
                  />
                </Field>
              </div>
              <Field label="Browser">
                <select
                  value={form.browser_type ?? ''}
                  onChange={(e) => setField('browser_type', e.target.value)}
                  className={input}
                >
                  {BROWSER_TYPES.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Batch size">
                <input
                  type="number"
                  min={1}
                  value={form.batch_size ?? ''}
                  onChange={(e) => setField('batch_size', parseInt(e.target.value) || 1)}
                  className={input}
                />
              </Field>
              <Field label="Pause between batches (s)">
                <input
                  type="number"
                  min={0}
                  value={form.sleep_seconds_between_batches ?? ''}
                  onChange={(e) => setField('sleep_seconds_between_batches', parseInt(e.target.value) || 0)}
                  className={input}
                />
              </Field>
              <Field label="Back off after a captcha (s)">
                <input
                  type="number"
                  min={0}
                  value={form.sleep_seconds_after_captcha ?? ''}
                  onChange={(e) => setField('sleep_seconds_after_captcha', parseInt(e.target.value) || 0)}
                  className={input}
                />
              </Field>
            </div>
            <div className="mt-3 -mx-4">
              <ToggleRow
                title="Use crawl4ai's cache"
                description="Serve previously crawled pages from crawl4ai's cache (revalidated). A forced re-crawl always bypasses it."
                checked={form.use_cache !== false}
                onChange={(v) => setField('use_cache', v)}
              />
            </div>
          </FetcherCard>

          <FetcherCard
            title="Connected extension"
            fetcher={byKey.BrowserPluginV2}

          >
            {renderPagesToggle}
            <ConnectedExtensionSettings />
          </FetcherCard>


          <FetcherCard title="Queued extension" fetcher={byKey.BrowserPlugin}>
            {renderPagesToggle}
            <QueuedExtensionSettings />
          </FetcherCard>

          <FetcherCard title="Serper" fetcher={byKey.Serper}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <Field
                  label="API key"
                  hint={
                    settings?.serper_api_key_set
                      ? 'A key is saved; leave blank to keep it.'
                      : settings?.serper_key_from_env
                        ? 'None saved: the HOUSE_SERPER_API_KEY environment value is in use.'
                        : 'Stored encrypted. Get one at serper.dev.'
                  }
                >
                  <input
                    type="password"
                    autoComplete="off"
                    value={form.serper_api_key ?? ''}
                    onChange={(e) => setField('serper_api_key', e.target.value)}
                    placeholder={settings?.serper_api_key_set ? '•••••••• (saved)' : 'Paste a serper.dev API key'}
                    className={`${input} font-mono`}
                  />
                </Field>
              </div>
              <Field label="Country (gl)">
                <input value={form.serper_country ?? ''} onChange={(e) => setField('serper_country', e.target.value)} className={input} />
              </Field>
              <Field label="Language (hl)">
                <input value={form.serper_language ?? ''} onChange={(e) => setField('serper_language', e.target.value)} className={input} />
              </Field>
              <div className="sm:col-span-2">
                <Field label="Location (optional)">
                  <input
                    value={form.serper_location ?? ''}
                    onChange={(e) => setField('serper_location', e.target.value)}
                    placeholder="e.g. New York, United States"
                    className={input}
                  />
                </Field>
              </div>
            </div>
          </FetcherCard>
        </FetcherTabs>
      </Section>

      {/* ───────────── Proxy lists ───────────── */}
      <Section
        title="Proxy lists"
        hint="Named lists of proxies, each used by the methods you tick. Every list serves every user's fetches; a method with no list fetches direct from our network."
      >
        <ProxyListsSettings onChanged={loadFetchers} />
      </Section>

      {/* ───────────── Adapters ───────────── */}
      <Section
        title="Who fetches what"
        hint="Tick the request types each adapter may run. Every request goes to the least busy adapter allowed to run it; if that one fails, another allowed one tries. They all run at once."
      >
        <div className="space-y-2">
          {(adapters?.adapters ?? []).map((info) => (
            <AdapterCard
              key={info.adapter}
              info={info}
              value={
                chosen[info.adapter] ?? {
                  on: info.on,
                  types: info.types,
                  concurrency: info.concurrency,
                  per_host: info.per_host,
                }
              }
              onChange={(next) => setAdapter(info.adapter, next)}
            />
          ))}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
          <Field
            label="Time limit for one fetch (seconds)"
            hint="Any fetch still running after this counts as a failed try, and another adapter gets it. 10 to 600."
          >
            <input
              type="number"
              min={10}
              max={600}
              value={form.fetch_timeout_seconds ?? ''}
              onChange={(e) =>
                setField('fetch_timeout_seconds', e.target.value === '' ? undefined : parseInt(e.target.value))
              }
              className={input}
            />
          </Field>
          <Field
            label="Share a proxy's block with similar sites (hours)"
            hint="When a site blocks a proxy, other sites using the same bot protection (DataDome, Cloudflare, ...) avoid that proxy this long while a clean one is free. 168 = a week. 0 = only the site that blocked it. Up to 720."
          >
            <input
              type="number"
              min={0}
              max={720}
              value={form.shared_block_hours ?? ''}
              onChange={(e) =>
                setField('shared_block_hours', e.target.value === '' ? undefined : parseInt(e.target.value))
              }
              className={input}
            />
          </Field>
          <Field label="Results per search">
            <input
              type="number"
              min={1}
              value={form.desired_serp_count ?? ''}
              onChange={(e) => setField('desired_serp_count', parseInt(e.target.value) || 1)}
              className={input}
            />
          </Field>
        </div>
      </Section>

      <Section title="Search engines" hint="Searches are dealt across these. Only engines some adapter may search can be picked.">
        <EnginePool
          options={engineOptions}
          selected={form.search_engines ?? []}
          mode={form.engine_mode ?? 'SPREAD'}
          onChange={(next) => setField('search_engines', next)}
          onMode={(next) => setField('engine_mode', next)}
        />
        <PausedEngines onChange={() => void loadFetchers()} />
      </Section>

      <Section title="Pages">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field
            label="Re-crawl pages after (days)"
            hint="Blank = never re-crawl while stored content exists; 0 = always."
          >
            <input
              type="number"
              min={0}
              step="any"
              placeholder="never"
              value={form.recrawl_after_days ?? ''}
              onChange={(e) => setField('recrawl_after_days', e.target.value === '' ? null : parseFloat(e.target.value))}
              className={input}
            />
          </Field>
        </div>
        <div className="-mx-4">
          <ToggleRow
            title="Extract facts from fetched pages"
            description="Run the AI extraction that pulls a summary and facts out of each page. Off = pages are stored with their clean text, but no LLM cost."
            checked={form.extract_page_facts !== false}
            onChange={(v) => setField('extract_page_facts', v)}
          />
        </div>
      </Section>

      {/* The queue and throughput are on their own tab: NetworkActivity. */}

      <div className="sticky bottom-0 py-3 bg-gradient-to-t from-gray-50 dark:from-gray-900 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 text-sm"
        >
          {saving ? 'Saving…' : 'Save network settings'}
        </button>
        {saved && <span className="text-sm text-green-600 dark:text-green-400">Saved</span>}
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Proxy lists, tokens and devices save as you change them.
        </span>
      </div>
    </div>
  );
}

/** WHICH ENGINES TO SEARCH WITH — a pool, not a pick. Availability comes from the server for the
 *  selected search source, because it depends on it (crawl4ai only parses DuckDuckGo). */
function EnginePool({
  options,
  selected,
  mode,
  onChange,
  onMode,
}: {
  options: EngineOption[];
  selected: string[];
  mode: string;
  onChange: (next: string[]) => void;
  onMode: (next: string) => void;
}) {
  const toggle = (engine: string) =>
    onChange(selected.includes(engine) ? selected.filter((e) => e !== engine) : [...selected, engine]);
  return (
    <div>
      <label className="block text-xs text-gray-600 dark:text-gray-300 mb-1">Search engines</label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
        {options.map((o) => (
          <label
            key={o.engine}
            className={`flex items-start gap-2 text-sm ${o.supported ? 'text-gray-700 dark:text-gray-200' : 'text-gray-400 dark:text-gray-500'}`}
            title={o.supported ? '' : o.note}
          >
            <input
              type="checkbox"
              disabled={!o.supported}
              checked={selected.includes(o.engine)}
              onChange={() => toggle(o.engine)}
              className="mt-0.5"
            />
            <span>
              {o.engine}
              {!o.supported && <span className="ml-1 text-xs">- unavailable: {o.note}</span>}
            </span>
          </label>
        ))}
      </div>
      {selected.length === 0 && (
        <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">Nothing selected - the default engine is used.</p>
      )}
      {selected.length > 1 && (
        <select value={mode} onChange={(e) => onMode(e.target.value)} className={`${input} mt-2`}>
          <option value="SPREAD">Spread - one engine per search, dealt across the pool (faster)</option>
          <option value="BROADCAST">Broadcast - ask every engine and merge (broader, slower)</option>
        </select>
      )}
    </div>
  );
}
